'use client';

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectDiscoveryMapMapBounds,
  selectDiscoveryMapCurrentTerritory,
  selectDiscoveryMapZoom,
  selectDiscoveryMapHeatMap,
  selectDiscoveryMapHasInvalidBounds,
} from 'src/redux/slices/discoveryMapSlice';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import { useSavedVolume } from 'src/hooks/savedVolume/useSavedVolume';
import { LOCATION } from 'src/hooks/filters/constants';
import useUserSettings from 'src/hooks/users/useUserSettings';
import useDiscoveryList from 'src/hooks/discovery/useDiscoveryList';
import type {
  DiscoveryListRequest,
  DiscoveryMapRequest,
  BaseDiscoveryRequestData,
} from 'src/api/redux/discovery';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isReadyCohortVolume } from 'src/utils/encounterLabels';

export const useDiscoveryListQueryParams = (): {
  queryParams: DiscoveryListRequest;
  skip: boolean;
} => {
  const { sort, order, page, pageSize, q } = useDiscoveryList();
  const { settings } = useUserSettings();
  const mapBounds = useSelector(selectDiscoveryMapMapBounds);
  const hasInvalidBounds = useSelector(selectDiscoveryMapHasInvalidBounds);
  const currentTerritory = useSelector(selectDiscoveryMapCurrentTerritory);
  const { apiFilterFormat, appliedDateRange } = useFilterSets(
    LOCATION.DISCOVERY
  );
  const { appliedSavedVolume } = useSavedVolume(LOCATION.DISCOVERY);
  const { showEncountersTotals } = useFlags();

  const type =
    !settings?.prospectType || settings?.prospectType === 'HCP'
      ? 'provider'
      : 'site';

  // A cohort search only gains its volume annotations (claims/patients/
  // encounters) once the cohort is READY. Other statuses (PROCESSING/ERROR/
  // DEACTIVATED) — or a missing id — mean no cohort_id is usable and no metric
  // is sortable, so such a search must never drive the sort or fire a request.
  const cohortId =
    appliedSavedVolume?.volumeType === 'cohort' ? appliedSavedVolume.id : null;
  const isCohortReady = !!cohortId && isReadyCohortVolume(appliedSavedVolume);

  // For encounters (cohort) searches, sort by the encounters metric so the
  // (unchanged) "Claims" sort options order by encounters. Only for a READY
  // cohort, whose cohort_id is sent and which has the encounters annotation.
  const effectiveSort =
    showEncountersTotals && isCohortReady && sort === 'claims'
      ? 'encounters'
      : sort;

  const params = useMemo(
    () => ({
      type: type as 'provider' | 'site',
      mode: settings?.prospectMode || 'treating',
      dateRange: {
        gte: appliedDateRange.gte,
        lte: appliedDateRange.lte,
      },
      sort: effectiveSort,
      order,
      page,
      pageSize,
      q,
    }),
    [
      type,
      settings?.prospectMode,
      appliedDateRange.gte,
      appliedDateRange.lte,
      effectiveSort,
      order,
      page,
      pageSize,
      q,
    ]
  );

  const data: BaseDiscoveryRequestData = useMemo(() => {
    const baseData = {
      territory: currentTerritory?.id || null,
      map_bounds: mapBounds,
      filter_sets: apiFilterFormat,
    };

    if (appliedSavedVolume?.volumeType === 'cohort') {
      return {
        ...baseData,
        cohort_id: appliedSavedVolume.id,
      };
    } else if (appliedSavedVolume?.volumeType === 'filterSet') {
      return {
        ...baseData,
        code_set: {
          type: appliedSavedVolume.type,
          filters:
            appliedSavedVolume.filters?.map((filter) => ({
              type: filter.type,
              values: filter.values,
              is_exclude: filter.is_exclude,
              is_all: filter.is_all,
            })) ?? [],
        },
      };
    } else {
      return {
        ...baseData,
        code_set: null,
        cohort_id: null,
      };
    }
  }, [currentTerritory?.id, mapBounds, apiFilterFormat, appliedSavedVolume]);

  const hasNoFilters =
    !data.cohort_id && !data.code_set && data.filter_sets.length === 0;
  // A cohort that isn't READY has no volume annotations, so any sort errors on
  // the backend — skip until it's ready rather than firing an invalid request.
  const isCohortNotReady =
    appliedSavedVolume?.volumeType === 'cohort' && !isCohortReady;

  return {
    queryParams: { params, data },
    skip: hasNoFilters || isCohortNotReady || hasInvalidBounds,
  };
};

export const useDiscoveryMapQueryParams = (): {
  queryParams: DiscoveryMapRequest;
  skip: boolean;
} => {
  const { settings } = useUserSettings();
  const mapBounds = useSelector(selectDiscoveryMapMapBounds);
  const hasInvalidBounds = useSelector(selectDiscoveryMapHasInvalidBounds);
  const currentTerritory = useSelector(selectDiscoveryMapCurrentTerritory);
  const zoom = useSelector(selectDiscoveryMapZoom);
  const heatMap = useSelector(selectDiscoveryMapHeatMap);
  const { apiFilterFormat, appliedDateRange } = useFilterSets(
    LOCATION.DISCOVERY
  );
  const { appliedSavedVolume } = useSavedVolume(LOCATION.DISCOVERY);

  const type =
    !settings?.prospectType || settings?.prospectType === 'HCP'
      ? 'provider'
      : 'site';

  const params = useMemo(
    () => ({
      type: type as 'provider' | 'site',
      mode: settings?.prospectMode || 'treating',
      dateRange: {
        gte: appliedDateRange.gte,
        lte: appliedDateRange.lte,
      },
      zoom,
      heatmap: heatMap,
    }),
    [
      type,
      settings?.prospectMode,
      appliedDateRange.gte,
      appliedDateRange.lte,
      zoom,
      heatMap,
    ]
  );

  const data: BaseDiscoveryRequestData = useMemo(() => {
    const baseData = {
      territory: currentTerritory?.id || null,
      map_bounds: mapBounds,
      filter_sets: apiFilterFormat,
    };

    if (appliedSavedVolume?.volumeType === 'cohort') {
      return {
        ...baseData,
        cohort_id: appliedSavedVolume.id,
      };
    } else if (appliedSavedVolume?.volumeType === 'filterSet') {
      return {
        ...baseData,
        code_set: {
          type: appliedSavedVolume.type,
          filters:
            appliedSavedVolume.filters?.map((filter) => ({
              type: filter.type,
              values: filter.values,
              is_exclude: filter.is_exclude,
              is_all: filter.is_all,
            })) ?? [],
        },
      };
    } else {
      return {
        ...baseData,
        code_set: null,
        cohort_id: null,
      };
    }
  }, [currentTerritory?.id, mapBounds, apiFilterFormat, appliedSavedVolume]);

  const hasNoFilters =
    !data.cohort_id && !data.code_set && data.filter_sets.length === 0;
  const isProcessing =
    appliedSavedVolume?.volumeType === 'cohort' &&
    appliedSavedVolume?.status === 'PROCESSING';

  return {
    queryParams: { params, data },
    skip: hasNoFilters || isProcessing || hasInvalidBounds,
  };
};
